import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {VetReservation} from "../models/vet-reservation.model";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetReservationsDgSearchResults} from "../models/datagrid-search-results/vet-reservations.dg-search-results.model";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Snapshot} from "../models/snpashot.model";
import {RefundRequest} from "../models/refund-request.model";
import {LabTestData} from "../models/lab-test-data.model";
import {ScheduledEmail} from "../models/scheduled-email.model";

/**
 * Serwis warstwy danych zarządzający Rezerwacją Wizyty (VetReservation).
 * Integruje logikę planowania (Scheduling), powiadomień (Push/Email), wyników badań laboratoryjnych
 * oraz powiązanych operacji finansowych (Refunds).
 */
@Injectable({
  providedIn: 'root'
})
export class VetReservationService implements DataGridServiceInterface<Observable<VetReservationsDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę rezerwacji dla głównego widoku tabeli (DataGrid).
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca (np. data wizyty).
   * @param showDeletedObjects Flaga decydująca o widoczności wizyt anulowanych/usuniętych.
   * @returns Observable emitujący obiekt wyników DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VetReservationsDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-reservations/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const vetReservationsResults = new VetReservationsDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetReservation(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return vetReservationsResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje i filtruje rezerwacje w oparciu o zaawansowane parametry.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VetReservationsDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/vet-reservations/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new VetReservationsDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetReservation(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Metoda zaślepka przygotowana pod przyszłą integrację
   */
  getThumbnails(uuid: string) {
    return [];
  }

  /**
   * Pobiera pełną historię zdarzeń (Dziennik Audytu) dla konkretnej rezerwacji.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-reservations/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełny model danych dla pojedynczej, wybranej rezerwacji.
   */
  getReservationByUUID(reservationUUID: string): Observable<VetReservation> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-reservations/${reservationUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedCopyEver<VetReservation>(new VetReservation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa rezerwację.
   */
  deleteReservation(uuid: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/vet-reservations/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetReservation>(new VetReservation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Przeprowadza manualną klasyfikację wizyty
   */
  classify(uuid: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-reservations/classify/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.message;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera globalne/ogólne saldo związane z modułem rezerwacji (np. łączna kwota opłaconych z góry wizyt).
   */
  getBalance(): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/balance`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.message;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wysyła baner informacyjny powiązany z zaplanowaną wizytą
   */
  sendBanner(uuid: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-reservations/send/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.message;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera powiązany wniosek o zwrot środków (RefundRequest).
   */
  showRefundObj(uuid: string): Observable<RefundRequest> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-reservations/refund/${uuid}`,).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<RefundRequest>(new RefundRequest(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Alternatywna metoda wyszukiwania rezerwacji na podstawie generycznego parametru.
   */
  getReservation(queryParam: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-res/${queryParam}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedCopyEver<VetReservation>(new VetReservation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje wyniki badań laboratoryjnych przypinając je bezpośrednio
   * do konkretnej rezerwacji/wizyty pacjenta.
   */
  sendLabTestData(labTestData: LabTestData) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/lab-test-db-data', labTestData).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedCopyEver<LabTestData>(new LabTestData(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Ręcznie wyzwala wysyłkę krytycznego powiadomienia do pacjenta
   * w przypadku istotnej zmiany dotyczącej wizyty.
   */
  informAboutImportantChanges(visitXpUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/visit-change-push/${visitXpUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera aktualny, autorytatywny czas z serwera.
   */
  getServerTime() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/get-server-time').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.currentServerTime;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera dziennik zaplanowanych i wysłanych wiadomości
   * e-mail powiązanych z konkretną wizytą.
   */
  checkEmailsForReservation(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-res-email-check/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedArrayCopyEver(new ScheduledEmail(), [], vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }
}
